import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import type { AdminGuideListItem } from '@/features/admin/api/admin.api';
import { StatusBadge } from '@/shared/components/ui/StatusBadge';
import {
  STATUS_DETAIL,
  STATUS_GROUP_BADGE,
  STATUS_GROUP_LABEL,
  guideDisplayName,
  guideInitials,
  statusGroupOf,
} from './guideDirectory.utils';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export function GuideIdentityCell({ guide, dense = false }: { guide: AdminGuideListItem; dense?: boolean }) {
  const name = guideDisplayName(guide);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Avatar
        src={guide.user?.avatar}
        alt=""
        sx={{
          width: dense ? 40 : 36,
          height: dense ? 40 : 36,
          fontSize: '0.8125rem',
          fontWeight: 700,
          bgcolor: 'primary.50',
          color: 'primary.main',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {guideInitials(name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap title={name} sx={{ color: 'text.primary' }}>
          {name}
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{
            display: 'block',
            fontFamily: guide.identity ? MONO : undefined,
            color: guide.identity ? 'text.secondary' : 'textColors.muted',
            letterSpacing: guide.identity ? '0.02em' : undefined,
          }}
        >
          {guide.identity?.guideId ?? 'No Guide ID yet'}
        </Typography>
      </Box>
    </Box>
  );
}

export function GuideStatusBadge({ status }: { status?: string }) {
  const group = statusGroupOf(status);
  const detail = status ? STATUS_DETAIL[status] : undefined;
  const badge = <StatusBadge status={STATUS_GROUP_BADGE[group]} label={STATUS_GROUP_LABEL[group]} />;
  return detail ? (
    <Tooltip title={detail} arrow>
      <span>{badge}</span>
    </Tooltip>
  ) : (
    badge
  );
}

export function VisibilityPill({ live }: { live: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: live ? 'var(--success-text, #15803D)' : 'text.secondary',
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: live ? 'var(--success)' : 'var(--text-light)',
          boxShadow: live ? '0 0 0 3px var(--success-light)' : 'none',
        }}
      />
      {live ? 'Live' : 'Hidden'}
    </Box>
  );
}

export function RatingValue({ rating, reviewCount }: { rating?: number; reviewCount?: number }) {
  if (!reviewCount || rating == null) {
    return (
      <Typography variant="body2" sx={{ color: 'textColors.muted', whiteSpace: 'nowrap' }}>
        Not rated
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
      <StarRoundedIcon sx={{ fontSize: 18, color: 'var(--gold)' }} aria-hidden />
      <Typography variant="body2" fontWeight={600}>
        {rating.toFixed(1)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        ({reviewCount})
      </Typography>
    </Box>
  );
}

export function ExperienceValue({ years }: { years?: number }) {
  if (years == null) {
    return (
      <Typography variant="body2" sx={{ color: 'textColors.muted' }}>
        —
      </Typography>
    );
  }
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
      {years} {years === 1 ? 'yr' : 'yrs'}
    </Typography>
  );
}
